import React from 'react';

import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives/compiled';

import { useDynamicString } from '../../hooks/useDynamicValue.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';
import type { A2UITextProps, TextVariant } from '../../types/A2UIComponents.types.tsx';
import { renderMarkdown } from '../../utils/markdown.tsx';

const headingSizeMap: Record<string, 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small'> = {
	h1: 'xxlarge',
	h2: 'xlarge',
	h3: 'large',
	h4: 'medium',
	h5: 'small',
};

const headingAsMap: Record<string, 'h1' | 'h2' | 'h3' | 'h4' | 'h5'> = {
	h1: 'h1',
	h2: 'h2',
	h3: 'h3',
	h4: 'h4',
	h5: 'h5',
};

type Props = {
	definition: A2UIComponentDefinition;
};

export const A2UIText = ({ definition }: Props): React.JSX.Element => {
	const def = definition as A2UITextProps;
	const text = useDynamicString(def.text);
	const variant: TextVariant = def.variant ?? 'body';

	const content = renderMarkdown(text);

	// Heading variants
	if (variant in headingSizeMap) {
		return (
			<Heading size={headingSizeMap[variant]} as={headingAsMap[variant]}>
				{content}
			</Heading>
		);
	}

	// Caption variant
	if (variant === 'caption') {
		return (
			<Text size="small" as="span">
				{content}
			</Text>
		);
	}

	// Default body
	return <Text as="span">{content}</Text>;
};
